import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { apiClient } from '../services/http.js';
import { PlanGrid } from '../components/Billing/PlanGrid.jsx';
import { fetchPlanCatalog } from '../services/planCatalog.js';
import { getPurchaseKind } from '../services/purchase/purchaseProvider.js';

const PERSONA_LABEL_PT = { padre_amigo: 'Padre Amigo', teologo: 'Teólogo', catequista: 'Catequista', diretor: 'Diretor Espiritual' };
const SUPPORT_LABEL_PT = { help_center: 'Central de ajuda', email: 'E-mail', priority: 'Prioritário', priority_sla: 'Prioritário com SLA' };

// Linhas da tabela comparativa: cada uma lê um campo estruturado de
// `entitlements` (não a lista de bullets em português, que varia de tamanho
// entre planos) para garantir que a célula da coluna N sempre representa o
// mesmo recurso em todas as linhas.
const COMPARISON_ROWS = [
  { label: 'Mensagens semanais', value: (p) => (p.is_trial ? '15 (não renováveis)' : `${p.credits_per_week} mensagens`) },
  { label: 'Acesso à Bíblia Sagrada', value: () => 'Sim (73 livros)' },
  { label: 'Personas de IA no chat', value: (p) => p.entitlements.chat_personas.map((k) => PERSONA_LABEL_PT[k] || k).join(', ') },
  { label: 'Sínodo Virtual', value: (p) => (p.entitlements.synod_enabled ? `Sim, ${p.entitlements.synod_credit_cost} créditos` : 'Não') },
  {
    label: 'Leitura Assistida',
    value: (p) => {
      const limit = p.entitlements.reading_active_plan_limit;
      if (limit === 0) return 'Apenas visualizar';
      if (limit === null) return 'Planos ativos ilimitados';
      return `Até ${limit} ativo(s)`;
    },
  },
  { label: '"Aprofundar com IA" na Leitura', value: (p) => (p.entitlements.reading_deepen_enabled ? 'Sim' : 'Não') },
  {
    label: 'Caderno Espiritual',
    value: (p) => (p.entitlements.notes_max_count === null ? 'Anotações ilimitadas' : `Até ${p.entitlements.notes_max_count} anotações`),
  },
  { label: 'Busca semântica no Caderno', value: (p) => (p.entitlements.notes_semantic_search ? 'Sim' : 'Não') },
  { label: 'Exportação do Caderno', value: (p) => (p.entitlements.notes_export ? 'Sim' : 'Não') },
  {
    label: 'Trilhas de estudo ativas',
    value: (p) => (p.entitlements.trails_active_limit === 0 ? 'Apenas visualizar' : `Até ${p.entitlements.trails_active_limit}`),
  },
  {
    label: 'Histórico de Insights',
    value: (p) => (p.entitlements.insights_history_days >= 3650 ? 'Completo' : `${p.entitlements.insights_history_days} dias`),
  },
  { label: 'Suporte ao cliente', value: (p) => SUPPORT_LABEL_PT[p.entitlements.support_level] || p.entitlements.support_level },
];

// Duas FAQs mencionavam "Stripe" e "portal do Stripe" incondicionalmente —
// texto direcionando pagamento fora da loja é motivo de rejeição na Guideline
// 3.1.1 se aparecer no bundle nativo (mesmo risco já tratado em PlanGrid.jsx
// e NativeSubscriptionControls.jsx; esta página tinha ficado de fora daquela
// auditoria porque o "Restaurar compras"/"Gerenciar assinatura" vive em
// outro componente). getPurchaseKind() é síncrono e não depende de rede.
const buildFaqs = (purchaseKind) => [
  {
    question: 'Como funcionam os créditos semanais?',
    answer: 'Cada mensagem enviada ao assistente consome 1 crédito. Os créditos são renovados semanalmente de forma automática com base no seu plano, garantindo que você tenha um fluxo constante para tirar suas dúvidas e estudar as Escrituras.',
  },
  {
    question: 'Posso cancelar a qualquer momento?',
    answer: purchaseKind === 'revenuecat'
      ? 'Sim, absolutamente. Você pode gerenciar ou cancelar sua assinatura a qualquer momento nas configurações de assinatura da App Store ou Google Play. O cancelamento entrará em vigor no final do período de cobrança contratado.'
      : 'Sim, absolutamente. Você pode gerenciar ou cancelar sua assinatura diretamente pela página de configurações da sua conta. O cancelamento entrará em vigor no final do período de cobrança contratado.',
  },
  {
    question: 'O CatholicGPT substitui o conselho de um sacerdote?',
    answer: 'De forma alguma. O CatholicGPT é uma ferramenta de estudo, formação e aprofundamento teológico baseada no Catecismo e no Magistério da Igreja. Dúvidas profundas de consciência, confissão e direção espiritual devem ser sempre tratadas diretamente com um padre.',
  },
  {
    question: 'Quais são as formas de pagamento aceitas?',
    answer: purchaseKind === 'revenuecat'
      ? 'A assinatura é processada pela App Store ou Google Play, usando a forma de pagamento já cadastrada na sua conta da loja.'
      : 'Aceitamos os principais cartões de crédito internacionais e nacionais através de um processador de pagamentos com conformidade PCI-DSS, que garante máxima segurança.',
  },
];

const PlanosPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [plans, setPlans] = useState([]);
  const FAQS = buildFaqs(getPurchaseKind());

  // Catálogo de planos vem do backend — preços, créditos e recursos em um só lugar.
  useEffect(() => {
    fetchPlanCatalog().then(setPlans);
  }, []);

  // Busca o plano atual apenas para usuários autenticados
  useEffect(() => {
    if (!isAuthenticated) return;
    apiClient.request('/billing/credits')
      .then(({ res, data }) => {
        if (res.ok && data?.plan) {
          setCurrentPlanId(data.plan);
        }
      })
      .catch(() => null);
  }, [isAuthenticated]);



  return (
    <div className="min-h-screen bg-background text-on-surface font-sans flex flex-col">
      {/* Cabeçalho */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-xl border-b border-outline-variant/10 shadow-sm px-4 md:px-6 py-3 md:py-4 flex items-center justify-between transition-colors duration-200">
        <div className="max-w-6xl mx-auto w-full flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-1 sm:gap-3 shrink-0">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-full border border-outline-variant/30 text-on-surface-variant hover:text-primary hover:border-primary/50 transition-all bg-surface-container-low text-xs sm:text-sm font-semibold"
            >
              <span className="material-symbols-outlined text-[16px] sm:text-[18px]">arrow_back</span>
              <span className="hidden sm:inline">Voltar</span>
            </button>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <img alt="CatholicGPT Logo" className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-white p-0.5 border border-primary/20" src="/logo-64.webp" />
            <span className="font-serif text-[1rem] sm:text-lg font-bold text-primary">CatholicGPT</span>
          </div>

          <div className="shrink-0">
            {isAuthenticated ? (
              <button
                onClick={() => navigate('/chat')}
                className="bg-primary text-on-primary px-3 sm:px-5 py-1.5 rounded-full text-[10px] sm:text-xs font-bold hover:opacity-90 transition-opacity"
              >
                Acessar Chat
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="bg-primary text-on-primary px-4 sm:px-5 py-1.5 rounded-full text-[10px] sm:text-xs font-bold hover:opacity-90 transition-opacity"
              >
                Entrar
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Conteúdo Principal */}
      <main className="flex-grow pt-28 pb-20 px-6 max-w-6xl mx-auto w-full space-y-16">
        
        {/* Intro */}
        <section className="text-center space-y-4 max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/5 text-primary text-xs font-semibold tracking-wider uppercase">
            <span className="material-symbols-outlined text-sm">workspace_premium</span>
            <span>Jornada de Formação</span>
          </div>
          <h1 className="font-serif text-3xl md:text-5xl font-bold tracking-tight text-on-surface leading-tight">
            Escolha o Plano Ideal para Sua <span className="text-primary italic">Caminhada de Fé</span>
          </h1>
          <p className="text-sm md:text-base text-on-surface-variant/80 font-light leading-relaxed">
            Tenha acesso às respostas do Catecismo, da Bíblia e da Tradição da Igreja sem interrupções.
          </p>
        </section>

        <PlanGrid plans={plans} currentPlanId={currentPlanId} />

        {/* Tabela de Comparação — inclui o Trial para deixar claro o que ele
            já oferece sem checkout (P23C: "Trial aparece sem checkout"). */}
        <section className="space-y-6 pt-8">
          <h2 className="font-serif text-2xl font-bold text-center text-on-surface">Comparativo de Recursos</h2>
          <div className="overflow-x-auto rounded-xl border border-outline-variant/20 shadow-sm bg-surface-container-lowest/30">
            <table className="w-full text-left border-collapse text-xs md:text-sm">
              <thead>
                <tr className="bg-surface-container-low border-b border-outline-variant/20 font-serif">
                  <th className="p-4 font-bold text-on-surface">Recurso</th>
                  {plans.map((plan) => (
                    <th key={plan.plan_id} className="p-4 font-semibold text-primary">{plan.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/10 text-on-surface-variant">
                {COMPARISON_ROWS.map((row) => (
                  <tr key={row.label}>
                    <td className="p-4 font-medium text-on-surface">{row.label}</td>
                    {plans.map((plan) => (
                      <td key={plan.plan_id} className="p-4">{row.value(plan)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQs */}
        <section className="space-y-8 pt-8 max-w-4xl mx-auto">
          <h2 className="font-serif text-2xl font-bold text-center text-on-surface">Perguntas Frequentes</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {FAQS.map((faq, idx) => (
              <div key={idx} className="bg-surface-container-low/50 p-6 rounded-xl border border-outline-variant/10 space-y-2">
                <h4 className="font-serif font-bold text-sm text-on-surface flex items-start gap-2">
                  <span className="text-primary text-base font-bold shrink-0">?</span>
                  <span>{faq.question}</span>
                </h4>
                <p className="text-xs text-on-surface-variant/80 font-light leading-relaxed pl-3">{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>

      </main>



      {/* Footer */}
      <footer className="w-full py-8 text-center border-t border-outline-variant/10 bg-surface-container-low/50 mt-12">
        <div className="text-[10px] tracking-[0.2em] text-on-surface-variant/40 uppercase font-sans">
          © 2026 CatholicGPT — Ad Maiorem Dei Gloriam
        </div>
      </footer>
    </div>
  );
};

export default PlanosPage;
